import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import constants from '@shared/constants'
import localStorageService from '@core/persistence/localStorage/localStorageService'
import type { UaePassSignupPrefill } from '../types/authTypes'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorageService.getItem(constants.LOCAL_STORAGE_KEYS.TOKEN))
  const refreshToken = ref<string | null>(
    localStorageService.getItem(constants.LOCAL_STORAGE_KEYS.REFRESH_TOKEN),
  )
  const verificationId = ref<string | null>(null)

  // Transient handoff from the UAE Pass callback to the sign-up confirm screen
  // (not persisted). The ticket is single-use server-side; prefill is the
  // verified identity to display on the confirm form.
  const uaePassSignupTicket = ref<string | null>(null)
  const uaePassSignupPrefill = ref<UaePassSignupPrefill | null>(null)

  const isAuthenticated = computed(() => Boolean(token.value))

  // Return target for the UAE Pass *link* flow (guest → investor upgrade from
  // the dashboard). Unset means the Account Settings enable flow.
  function setUaePassLinkReturnTo(path: string) {
    localStorageService.setItem(constants.LOCAL_STORAGE_KEYS.UAE_PASS_LINK_RETURN_TO, path)
  }

  // Read-once: the callback consumes it so a later Settings link isn't
  // hijacked by a stale dashboard intent.
  function consumeUaePassLinkReturnTo(): string | null {
    const path = localStorageService.getItem(constants.LOCAL_STORAGE_KEYS.UAE_PASS_LINK_RETURN_TO)
    localStorageService.removeItem(constants.LOCAL_STORAGE_KEYS.UAE_PASS_LINK_RETURN_TO)
    return path
  }

  function setToken(value: string) {
    token.value = value
    localStorageService.setItem(constants.LOCAL_STORAGE_KEYS.TOKEN, value)
  }

  function setRefreshToken(value: string) {
    refreshToken.value = value
    localStorageService.setItem(constants.LOCAL_STORAGE_KEYS.REFRESH_TOKEN, value)
  }

  function setVerificationId(value: string | null) {
    verificationId.value = value
  }

  function setUaePassSignup(ticket: string, prefill: UaePassSignupPrefill) {
    uaePassSignupTicket.value = ticket
    uaePassSignupPrefill.value = prefill
  }

  function clearUaePassSignup() {
    uaePassSignupTicket.value = null
    uaePassSignupPrefill.value = null
  }

  function logout() {
    token.value = null
    refreshToken.value = null
    verificationId.value = null
    uaePassSignupTicket.value = null
    uaePassSignupPrefill.value = null
    localStorageService.removeItem(constants.LOCAL_STORAGE_KEYS.TOKEN)
    localStorageService.removeItem(constants.LOCAL_STORAGE_KEYS.REFRESH_TOKEN)
    localStorageService.removeItem(constants.LOCAL_STORAGE_KEYS.UAE_PASS_LINK_RETURN_TO)
  }

  return {
    token,
    refreshToken,
    verificationId,
    uaePassSignupTicket,
    uaePassSignupPrefill,
    isAuthenticated,
    setToken,
    setRefreshToken,
    setVerificationId,
    setUaePassSignup,
    clearUaePassSignup,
    setUaePassLinkReturnTo,
    consumeUaePassLinkReturnTo,
    logout,
  }
})
